import React, { useState, useEffect } from 'react';
import { Plus, X, Search, Eye, Edit2, Trash2, FileText, Mail, Download } from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { genericInvoiceAPI } from "../../services/api";
import { toast } from "react-toastify";
import { getWhatsAppNumber } from '../Templates/templateUtils';
import EmailTemplatePanel from '../Templates/EmailTemplatePanel';
import WhatsAppTemplatePanel from '../Templates/WhatsAppTemplatePanel';
import Delete from "../common/Delete";
import CreateGenericInvoiceModal from "./CreateGenericInvoiceModal";
import EditGenericInvoiceModal from "./EditGenericInvoiceModal";
import InvoicePreviewModal from "./InvoicePreviewModal";

const GenericInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState(null);
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [emailPanelInvoice, setEmailPanelInvoice] = useState(null);
  const [whatsappPanelInvoice, setWhatsappPanelInvoice] = useState(null);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customerName: ''
  });

  // Fetch all invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await genericInvoiceAPI.getAllInvoices();
      let invoicesData = Array.isArray(response.data.invoices) 
        ? response.data.invoices 
        : response.data.content || [];
      setInvoices(invoicesData);
      setFilteredInvoices(invoicesData);
      console.log('Generic Invoices loaded:', response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...invoices];

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter(inv => {
        const invoiceDate = new Date(inv.invoiceDate);
        return invoiceDate >= new Date(filters.startDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(inv => {
        const invoiceDate = new Date(inv.invoiceDate);
        return invoiceDate <= new Date(filters.endDate);
      });
    }

    // Filter by customer name
    if (filters.customerName) {
      filtered = filtered.filter(inv =>
        inv.customerName?.toLowerCase().includes(filters.customerName.toLowerCase())
      );
    }

    // Search query
    if (searchQuery) {
      filtered = filtered.filter(inv =>
        inv.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.customerPO?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.legalBusinessName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredInvoices(filtered);
  }, [filters, invoices, searchQuery]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      customerName: ''
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredInvoices.map(inv => inv.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    try {
      for (const id of selectedRows) {
        await genericInvoiceAPI.deleteInvoice(id);
      }
      toast.success(`${selectedRows.length} invoice(s) deleted successfully`);
      setSelectedRows([]);
      setShowDeleteModal(false);
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoices:', error);
      toast.error('Failed to delete invoice(s)');
    }
  };

  const handleEdit = (invoice) => {
    setSelectedInvoiceForEdit(invoice);
    setShowEditModal(true);
  };

  const handlePreview = (invoice) => {
    setSelectedInvoiceForPreview(invoice);
    setShowPreviewModal(true);
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await genericInvoiceAPI.getInvoicePdf(invoice.id);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Generic Invoices</h1>
            <p className="text-sm text-gray-500 mt-1">
              {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus size={16} />
              Create Invoice
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedRows.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-md flex items-center justify-between">
            <span className="text-sm text-blue-900 font-medium">
              {selectedRows.length} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-3 py-1 text-sm text-red-700 hover:bg-red-100 rounded"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedRows([])}
                className="p-1 text-blue-700 hover:bg-blue-100 rounded"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Customer Name Filter */}
            <div className="w-48">
              <input
                type="text"
                placeholder="Customer Name"
                value={filters.customerName}
                onChange={(e) => handleFilterChange('customerName', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              />
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                placeholder="Start Date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-36 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <span className="text-gray-500">-</span>
              <input
                type="date"
                placeholder="End Date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-36 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            {/* Clear Filters */}
            {(filters.customerName || filters.startDate || filters.endDate) && (
              <button 
                onClick={clearAllFilters}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded-md"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center justify-between p-4 border-b gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search by invoice number, customer, PO number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <FileText size={48} className="mb-4 text-gray-300" />
            <p className="text-lg font-medium">No invoices found</p>
            <p className="text-sm mt-1">Try adjusting your filters or create a new invoice</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="w-12 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === filteredInvoices.length && filteredInvoices.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subtotal
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GST
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net Payable
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  onMouseEnter={() => setHoveredRow(invoice.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  className={`group hover:bg-gray-50 ${
                    selectedRows.includes(invoice.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(invoice.id)}
                      onChange={() => handleRowSelect(invoice.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">
                    {invoice.invoiceNumber || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    <div>
                      <div className="flex items-center gap-1.5 font-medium">
                        <span>{invoice.customerName || '-'}</span>
                        {invoice.customerEmail && (
                          <button onClick={e => { e.stopPropagation(); setEmailPanelInvoice(invoice); }}
                            title="Send email from template"
                            className="opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded text-blue-500 hover:text-blue-600 hover:bg-blue-50 cursor-pointer">
                            <Mail size={13} />
                          </button>
                        )}
                        {getWhatsAppNumber({ phone: invoice.customerPhone }) && (
                          <button onClick={e => { e.stopPropagation(); setWhatsappPanelInvoice(invoice); }}
                            title="Send WhatsApp"
                            className="opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded text-[#25D366] hover:bg-green-50 cursor-pointer">
                            <IoLogoWhatsapp size={13} />
                          </button>
                        )}
                      </div>
                      {invoice.customerGSTIN && (
                        <div className="text-xs text-gray-500">GSTIN: {invoice.customerGSTIN}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {invoice.legalBusinessName || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {invoice.items?.length || 0} items
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatCurrency(invoice.subTotal)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatCurrency(invoice.gstAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-semibold whitespace-nowrap">
                    {formatCurrency(invoice.netPayable)}
                  </td>
                  <td className="px-4 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
                    {hoveredRow === invoice.id && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePreview(invoice)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          title="Preview invoice"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(invoice)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          title="Edit invoice"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDownloadPdf(invoice)}
                          className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                          title="Download PDF"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRows([invoice.id]);
                            setShowDeleteModal(true);
                          }}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          title="Delete invoice"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateGenericInvoiceModal
          onClose={() => setShowCreateModal(false)}
          onCreate={() => {
            setShowCreateModal(false);
            fetchInvoices();
          }}
        />
      )}

      {showEditModal && selectedInvoiceForEdit && (
        <EditGenericInvoiceModal
          invoice={selectedInvoiceForEdit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedInvoiceForEdit(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setSelectedInvoiceForEdit(null);
            fetchInvoices();
          }}
        />
      )}

      {showPreviewModal && selectedInvoiceForPreview && (
        <InvoicePreviewModal
          invoice={selectedInvoiceForPreview}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedInvoiceForPreview(null);
          }}
        />
      )}

      {showDeleteModal && (
        <Delete
          count={selectedRows.length}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          entityName="invoice"
        />
      )}

      <EmailTemplatePanel
        isOpen={Boolean(emailPanelInvoice)}
        onClose={() => setEmailPanelInvoice(null)}
        module="INVOICE"
        context={emailPanelInvoice ? {
          clientName: emailPanelInvoice.customerName || '',
          invoiceNo: emailPanelInvoice.invoiceNumber || '',
          invoiceAmount: emailPanelInvoice.netPayable ? String(emailPanelInvoice.netPayable) : '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={emailPanelInvoice ? {
          email: emailPanelInvoice.customerEmail,
          name: emailPanelInvoice.customerName || '',
        } : null}
      />
      <WhatsAppTemplatePanel
        isOpen={Boolean(whatsappPanelInvoice)}
        onClose={() => setWhatsappPanelInvoice(null)}
        module="INVOICE"
        context={whatsappPanelInvoice ? {
          clientName: whatsappPanelInvoice.customerName || '',
          invoiceNo: whatsappPanelInvoice.invoiceNumber || '',
          invoiceAmount: whatsappPanelInvoice.netPayable ? String(whatsappPanelInvoice.netPayable) : '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={whatsappPanelInvoice ? {
          phone: getWhatsAppNumber({ phone: whatsappPanelInvoice.customerPhone }),
          phoneSource: 'phone',
          name: whatsappPanelInvoice.customerName || '',
        } : null}
      />
    </div>
  );
};

export default GenericInvoices;